import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {RootState} from "../store";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import {logout, setToken} from "../features/authSlice";
import {API_REFRESHTOKEN_ENDPOINT} from "../../constants/endpoints";
import {Mutex} from "async-mutex";

const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === "true";
const mask = (s?: string | null) =>
  s ? s.replace(/^(.{2}).*(.{2})$/, "$1***$2") : "";
const describeArgs = (args: string | FetchArgs) =>
  typeof args === "string"
    ? args
    : {
        url: args.url,
        method: args.method ?? "GET",
        hasBody: Boolean(args.body),
      };

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_IDMS_API_URL,
  prepareHeaders: (headers, {getState, endpoint}) => {
    const state = getState() as RootState;
    const accessToken = state.auth?.token?.accessToken;

    if (accessToken && endpoint !== "login") {
      headers.set("Content-Type", "application/json");
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  if (DEBUG_AUTH) {
    console.debug("[api] request", describeArgs(args));
  }
  let result = await baseQuery(args, api, extraOptions);
  if (DEBUG_AUTH) {
    console.debug("[api] response", {
      ok: !result.error,
      status: (result as any)?.meta?.response?.status,
      error: result.error && {
        status: (result.error as any)?.status,
        dataType: typeof (result.error as any)?.data,
      },
    });
  }

  if (result.error && result.error.status === 401) {
    // normalize args to get the requested URL
    const url = typeof args === "string" ? args : args.url;

    // Skip refresh if this is the login API itself
    if (url.includes("/api/v1/Account/token")) {
      return result;
    }

    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        // try to get a new token
        if (DEBUG_AUTH) {
          const st = api.getState() as RootState;
          console.debug("[api] attempting refresh", {
            url: API_REFRESHTOKEN_ENDPOINT,
            refreshTokenLen: (st.auth.token.refreshToken || "").length,
            clientId: process.env.REACT_APP_CLIENT_ID,
            clientSecret: mask(process.env.REACT_APP_CLIENT_SECRET as string),
          });
        }
        const refreshResult = await baseQuery(
          {
            url: API_REFRESHTOKEN_ENDPOINT,
            method: "POST",
            body: JSON.stringify({
              refreshToken: (api.getState() as RootState).auth.token
                .refreshToken,
              clientId: process.env.REACT_APP_CLIENT_ID,
              clientSecret: process.env.REACT_APP_CLIENT_SECRET,
            }),
          },
          api,
          extraOptions,
        );

        if (refreshResult.data) {
          // store the new token
          const loginResponse = refreshResult.data as LoginResponse;
          api.dispatch(
            setToken({
              accessToken: loginResponse.access_token,
              expiresIn: loginResponse.expires_in,
              refreshToken: loginResponse.refresh_token,
              tokenType: loginResponse.token_type,
              scope: loginResponse.scope,
            }),
          );
          if (DEBUG_AUTH) {
            console.debug("[api] refresh success; retrying original request");
          }
          // retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          if (DEBUG_AUTH) {
            console.debug("[api] refresh failed; logging out and redirecting");
          }
          api.dispatch(logout());
          // move to login page
          window.location.href = "/login";
        }
      } catch (e) {
        if (DEBUG_AUTH) {
          console.debug("[api] refresh error", e);
        } else {
          console.error(e);
        }
        api.dispatch(logout());
        // move to login page
        window.location.href = "/login";
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};

export const idmsApi = createApi({
  reducerPath: "idmsApi",
  baseQuery: baseQueryWithReauth,
  // global configuration for the api
  // keepUnusedDataFor: 600,
  // Add domain-specific tags to support cache invalidation across pages
  tagTypes: [
    "Auth",
    "User",
    "UserRole",
    "RoleUser",
    "UserProduct",
    // Target Manager specific
    "Product",
    "ProductFeatures",
    "ProductSubscriptions",
    "SubscriptionFeatures",
    "AddOns",
  ],
  endpoints: () => ({}),
});
